import {login as apiLogin, logout as apiLogout, permissions as fetchPermissions} from '@/services/authService/authService';
import {negotiateCryptoSession, clearCryptoSession, isPayloadCipherEnabled, warmCryptoHandshake} from '@/services/cryptoSession';
import {prefetchAuthMenu} from '@/services/authService/authMenu';
import {AuthProvider} from 'react-admin';
import type {QueryClient} from '@tanstack/react-query';
import MD5 from 'crypto-js/md5';
import userStore from '@/store/admin/userStore';
import {BASE_AUTH_API, fetchAPI, handleSessionExpired} from '@/services/api';
import {UserInfo} from '@/store/admin/userInfo';
import { emitForcePasswordChange } from '@/services/forcePasswordChange';
import { isSessionExpiredPending } from '@/services/sessionExpired';
import { clearContainerMemoryWarnPending, markContainerMemoryWarnPending } from '@/utils/serviceMonitorAccess';

// LocalStorage keys and helpers for reducing backend calls
const LS_USER = 'adminUserInfo';
const LS_PERMS = 'adminPermissions';
const loadJson = (key: string) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};
const saveJson = (key: string, value: any) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore quota/serialize errors
    }
};

/**
 * Mirror token into a non-HttpOnly cookie so bare browser GET (JSONViewer / address bar)
 * can pass Gateway PermissionFilter (reads adminToken / Admin-Token cookie).
 * WHY: SPA uses localStorage headers; plugin navigation has no Admin-Token header.
 */
const syncAdminTokenCookie = (token: string | null) => {
    if (typeof document === 'undefined') return;
    if (token) {
        document.cookie = `adminToken=${encodeURIComponent(token)}; path=/; SameSite=Lax`;
        document.cookie = `Admin-Token=${encodeURIComponent(token)}; path=/; SameSite=Lax`;
        return;
    }
    document.cookie = 'adminToken=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = 'Admin-Token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
};
const toIdentity = (user: any) => ({
    id: user.userId,
    fullName: user.username || ((user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : `${user.userId}`),
    avatar: user.img || '',
});
// Simple in-memory flags/caches for the current runtime session
let didIntrospect = false;
let cachedPermissions: any = null;
let cachedIdentity: any = null;
let logoutCalledOnce = false; // 防止本会话内重复调用后端登出

// Prevent repeated introspect calls after logout / auth failure storms
let isLoggedOut = false;
// Bumped on successful token write so an in-flight logout cannot clear a newer session
let authSessionId = 0;
let lastIntrospectAt = 0;
let lastIntrospectErrorAt = 0;
let introspectInFlight: Promise<UserInfo> | null = null;
let introspectAbortController: AbortController | null = null;

const INTROSPECT_COOLDOWN_MS = 10_000;
const INTROSPECT_TTL_MS = 60_000;

const clearAllClientStorageAndCookies = () => {
    try {
        localStorage.clear();
        sessionStorage.clear();
    } catch {
        // ignore
    }
    try {
        if (typeof document !== 'undefined') {
            const cookies = document.cookie ? document.cookie.split(';') : [];
            for (const cookie of cookies) {
                const eqPos = cookie.indexOf('=');
                const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
                if (!name) continue;
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
            }
        }
    } catch {
        // ignore
    }
};

const hasLocalToken = () => typeof localStorage !== 'undefined'
    && (!!localStorage.getItem('adminToken') || !!localStorage.getItem('Admin-Token'));

const cancelInFlightIntrospect = () => {
    try {
        introspectAbortController?.abort();
    } catch {
        // ignore
    } finally {
        introspectAbortController = null;
        introspectInFlight = null;
    }
};

const setLoggedOut = () => {
    isLoggedOut = true;
    didIntrospect = false;
    cachedPermissions = null;
    cachedIdentity = null;
    cancelInFlightIntrospect();
};

const ensureIntrospected = async () => {
    if (isLoggedOut) {
        throw new Error('Logged out');
    }
    if (didIntrospect) return;

    // If we have introspected recently in this runtime session, do not call backend again.
    if (lastIntrospectAt && (Date.now() - lastIntrospectAt) < INTROSPECT_TTL_MS) {
        didIntrospect = true;
        return;
    }

    // Avoid repeated calls when session is already invalid and React-Admin keeps calling checkAuth
    if (lastIntrospectErrorAt && (Date.now() - lastIntrospectErrorAt) < INTROSPECT_COOLDOWN_MS) {
        throw new Error('Introspect cooldown');
    }

    const localUser = loadJson(LS_USER);
    if (localUser) {
        userStore.setUser(localUser);
        didIntrospect = true;
        lastIntrospectAt = Date.now();
        return;
    }

    // 没有 token 且没有本地缓存，无需发起 introspect 请求，直接视为未登录
    if (!hasLocalToken()) {
        lastIntrospectErrorAt = Date.now();
        throw new Error('No token available');
    }

    if (!introspectInFlight) {
        introspectAbortController = new AbortController();
        introspectInFlight = fetchAPI<UserInfo>(BASE_AUTH_API + `/introspect`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            signal: introspectAbortController.signal,
        }).finally(() => {
            introspectAbortController = null;
            introspectInFlight = null;
        });
    }

    try {
        const user = await introspectInFlight;
        userStore.setUser(user);
        saveJson(LS_USER, user);
        didIntrospect = true;
        lastIntrospectAt = Date.now();
        lastIntrospectErrorAt = 0;
    } catch (e) {
        lastIntrospectErrorAt = Date.now();
        throw e;
    }
};

const authProvider: AuthProvider = {

    login: async ({username, password}) => {
        const loginType = 0;
        const salt = 'zhuo_yi';
        const plainPassword = password;
        for (let attempt = 0; attempt < 2; attempt++) {
            try {
                warmCryptoHandshake();
                const md5Password = MD5(password).toString();
                const data = await apiLogin({username, password: md5Password, loginType, salt});
                // 将 token 写到 localStorage，供 httpClient 追加到 Authorization 头使用
                localStorage.setItem('adminToken', data.adminToken);
                // WHY: browser/JSONViewer GET has no localStorage header; cookie unlocks Gateway auth
                syncAdminTokenCookie(data.adminToken);
                // Drop prior cipher session before non-blocking renegotiate (new token ≠ old AES)
                clearCryptoSession();
                // Invalidate any in-flight logout that would wipe this fresh token
                authSessionId += 1;
                isLoggedOut = false;

                if (data?.needChangePassword) {
                    try {
                        emitForcePasswordChange({
                            message: 'Password expired, please change password!',
                            username,
                            password: plainPassword,
                        });
                    } catch {
                        // ignore
                    }
                    const err: any = new Error('Password expired, please change password!');
                    err.code = 303;
                    throw err;
                }

                // Crypto + introspect/perms must NOT block redirect: yesterday's await Promise.all
                // moved dashboard wait onto the login spinner (often >2s). Menu is prefetched
                // after login via withAuthQueryClient; fetchAPI shares negotiateInFlight.
                if (isPayloadCipherEnabled()) {
                    void negotiateCryptoSession().catch(() => {
                        // menu/introspect will retry negotiate via fetchAPI
                    });
                }
                lastIntrospectErrorAt = 0;
                cancelInFlightIntrospect();
                void (async () => {
                    try {
                        if (isPayloadCipherEnabled()) {
                            await negotiateCryptoSession();
                        }
                        const [userResult, permsResult] = await Promise.all([
                            fetchAPI<UserInfo>(BASE_AUTH_API + `/introspect`, {
                                method: 'POST',
                                headers: {'Content-Type': 'application/json'},
                            }),
                            fetchPermissions().catch(() => null),
                        ]);
                        userStore.setUser(userResult);
                        saveJson(LS_USER, userResult);
                        didIntrospect = true;
                        lastIntrospectAt = Date.now();
                        cachedIdentity = toIdentity(userResult);
                        if (permsResult) {
                            cachedPermissions = permsResult;
                            saveJson(LS_PERMS, permsResult);
                        } else {
                            cachedPermissions = null;
                        }
                    } catch (e: any) {
                        if (e?.code === 303) {
                            try {
                                emitForcePasswordChange({
                                    message: e?.message || 'Password expired, please change password!',
                                    username,
                                    password: plainPassword,
                                });
                            } catch {
                                // ignore
                            }
                        }
                    }
                })();

                markContainerMemoryWarnPending();
                logoutCalledOnce = false;
                // 显式告知 React-Admin 登录后跳转到主页（对象形式兼容不同版本）
                return {redirectTo: '/'} as any;
            } catch (e: any) {
                if (e?.code === 1001 && attempt === 0) {
                    clearAllClientStorageAndCookies();
                    continue;
                }
                if (e?.code === 1001) {
                    const err: any = new Error('Login failed');
                    err.code = 1001;
                    throw err;
                }
                throw e;
            }
        }

        const err: any = new Error('Login failed');
        err.code = 1001;
        throw err;
    },

    logout: async () => {
        const sessionAtStart = authSessionId;
        setLoggedOut();
        // 若已经调用过后端 logout，则不再访问后端
        if (!logoutCalledOnce) {
            try {
                await apiLogout()
            } finally {
                // 先通知后端退出，保证服务器态清理；无论是否有 token，都只调用一次
                // 若期间已重新登录，勿标记，避免跳过后续真实登出
                if (sessionAtStart === authSessionId) {
                    logoutCalledOnce = true;
                }
            }
        }

        // 登录已写入更新的 session 时，跳过清理，避免清掉新 token 导致无法进 dashboard
        if (sessionAtStart !== authSessionId) {
            return Promise.resolve();
        }

        // 然后清理本地状态，确保 UI 立即跳转并短路后续请求
        userStore.clearUser();
        localStorage.removeItem('adminToken');
        // 同时清理历史遗留的 Admin-Token，避免启用条件误判
        localStorage.removeItem('Admin-Token');
        syncAdminTokenCookie(null);
        localStorage.removeItem(LS_USER);
        localStorage.removeItem(LS_PERMS);
        clearContainerMemoryWarnPending();
        clearCryptoSession();
        didIntrospect = false;
        lastIntrospectAt = 0;
        lastIntrospectErrorAt = 0;
        cachedPermissions = null;
        cachedIdentity = null;

        return Promise.resolve();
    },

    checkError: async (error: any) => {
        const code = error?.code;
        const status = error?.status || error?.response?.status;

        // 302 = Redis token expired; 4601 = cipher session missing after token expiry (UAT)
        // Show SessionExpiredDialog; do not RA-redirect yet
        if (code === 302 || code === 4601 || status === 302) {
            setLoggedOut();
            handleSessionExpired(302, error?.message);
            return Promise.resolve();
        }

        // 401 can also mean "no permission" in this system: do NOT logout
        if (code === 401) {
            return Promise.resolve();
        }

        if (status === 401 || status === 403) {
            setLoggedOut();
            // 遇到 401/403 时直接 reject；外层可再调 introspect 确认
            return Promise.reject();
        }
        return Promise.resolve();
    },

    checkAuth: async () => {
        try {
            // Dialog owns redirect while open (Confirm / 10s countdown)
            if (isSessionExpiredPending()) {
                return Promise.resolve();
            }

            if (isLoggedOut) {
                return Promise.reject({redirectTo: '/login', message: false} as any);
            }

            // localStorage token 存在时：直接通过（避免无意义的 introspect）
            // token 不存在时：尝试用 HttpOnly Cookie 的 session 进行 introspect
            if (!hasLocalToken()) {
                if (lastIntrospectAt && (Date.now() - lastIntrospectAt) < INTROSPECT_TTL_MS) {
                    return Promise.resolve();
                }
                await ensureIntrospected();
            } else {
                // WHY: refresh cookie for already-logged-in tabs so GET/JSONViewer works without re-login
                const existingToken =
                    localStorage.getItem('adminToken') || localStorage.getItem('Admin-Token');
                if (existingToken) {
                    syncAdminTokenCookie(existingToken);
                }
            }
            return Promise.resolve();
        } catch (e) {
            // 失败则视为未登录
            return Promise.reject({redirectTo: '/login', message: false} as any);
        }
    },

    getPermissions: async () => {
        if (!hasLocalToken() && !didIntrospect) {
            try {
                await ensureIntrospected();
            } catch {
                return [] as any;
            }
        }
        if (cachedPermissions) return cachedPermissions;
        // 优先从本地缓存恢复，避免访问后端
        const localPerms = loadJson(LS_PERMS);
        if (localPerms) {
            cachedPermissions = localPerms;
            return Promise.resolve(localPerms);
        }
        try {
            const {permissions} = await import('./authService');
            const result = await permissions();
            cachedPermissions = result;
            saveJson(LS_PERMS, result);
            return Promise.resolve(result);
        } catch (e) {
            return Promise.reject(e);
        }
    },

    getIdentity: async () => {
        if (!hasLocalToken() && !didIntrospect) {
            await ensureIntrospected();
        }
        if (cachedIdentity) return Promise.resolve(cachedIdentity);
        // 优先使用已在内存/本地的用户信息，避免访问后端
        if (userStore.user) {
            const identity = toIdentity(userStore.user);
            cachedIdentity = identity;
            return Promise.resolve(identity);
        }
        const localUser = loadJson(LS_USER);
        if (localUser) {
            userStore.setUser(localUser);
            const identity = toIdentity(localUser);
            cachedIdentity = identity;
            return Promise.resolve(identity);
        }
        const {introspect} = await import('./authService');
        const user = await introspect();
        userStore.setUser(user);
        saveJson(LS_USER, user);
        const identity = toIdentity(user);
        cachedIdentity = identity;
        return Promise.resolve(identity);
    }
};

/**
 * React-Admin 的 useLogin 在登录成功后不会清除 checkAuth 的 React Query 缓存。
 * 若此前 checkAuth 曾失败（例如访问受保护路由时），登录后仍会读到旧的失败状态，
 * 触发 LogoutOnMount 回到登录页，需二次点击或刷新才能进入系统。
 */
export function withAuthQueryClient(
    provider: AuthProvider,
    queryClient: QueryClient,
): AuthProvider {
    return {
        ...provider,
        login: async (params) => {
            const result = await provider.login(params) as {
                redirectTo?: string;
            };
            // Start menu fetch as soon as token exists; shares crypto negotiate + Query cache with sidebar
            void prefetchAuthMenu(queryClient);
            await queryClient.resetQueries({queryKey: ['auth', 'checkAuth']});
            return {redirectTo: result?.redirectTo ?? '/'} as any;
        },
    };
}

export default authProvider;
